<script>
    import { createEventDispatcher, onDestroy, onMount } from "svelte";

    import {
        calculateNewTimeByTimeBlocks,
        calculatePosition,
        calculateTimeFromPosition,
        calculateWidth,
        formatTime,
        getFormatHHMM,
        getShortDoctorName,
        getTimeBlocksByTime,
        handleResize,
        handleSelectArea,
        mapAndMergeEncountersAndEvents,
    } from "../helpers/dailyCalendarHelper.svelte";

    import {
        getTimeAfterChanges,
        resetCalendarRecurrenceEvent,
        syncCalendarEventDate,
    } from "../helpers/calendarHelper.svelte";

    import {
        dailyPlannerHourLabelWidth,
        EncounterSidePanelProps,
        prePopulateAccountOptions,
        user,
    } from "../../../stores";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getAccountCalendarEventType,
        getDateFromDateString,
        getDaysDifferFrom,
        getDisplayDate,
        getEncounterDischargeDateWithFallBackDuration,
        getOptionLabelForSurgeon,
        getTimeDiffInMillSec,
        getYYYYMMDD,
        goToURL,
        isObjectEmpty,
        openViewEncounterNewPage,
        replaceURL,
        showError,
        syncDefaultDoctorFilter,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        NotificationModal,
        PageLoading,
        SearchInput,
        DefaultDoctorFilter,
    } from "../../../Components/Common/index.svelte";

    import {
        DailyPlannerEventResizeBorderWidth,
        defaultCalendarEventColor,
        DirectCalendarEventTypes,
        editIcon,
    } from "../../../Utils/utilConstants.svelte";

    import CalendarRecurrenceModal from "../components/CalendarRecurrenceModal.svelte";
    import DailyCalendarExpandSidePanel from "../components/DailyCalendarExpandSidePanel.svelte";
    import DailyCalendarEncounterTooltip from "../components/DailyCalendarEncounterTooltip.svelte";
    import ModifyAccountEventModal from "../../Account/components/ModifyAccountEventModal.svelte";
    import CalendarEventCategorySelector from "../../CalendarCategories/CalendarEventCategorySelector.svelte";
    import {
        accountCalendarEventType,
        BillingStatus,
        EditRepeatingEventOptions,
    } from "../../../Utils/utilEnums.svelte";
    import SearchSelect from "../../../Components/Common/SearchSelect.svelte";
    import ResetFilterLink from "../../../Components/Common/ResetFilterLink.svelte";
    import CreateSurgeonModal from "../../PatientEncounter/components/CreateSurgeonModal.svelte";
    import Select from "svelte-select/Select.svelte";
    import CalendarEventsNestedEncounterModal from "../components/CalendarEventsNestedEncounterModal.svelte";
    import debounce from "lodash/debounce";
    import {
        loadAndUpdatePatientEncounterFields,
        updateAccountCalendarEvent,
    } from "../../../Utils/DataLoaders.svelte";
    import { cacheAccountPrePopulateSearchOptions } from "../../../Utils/utilCache.svelte";
    import SurgeonSelector from "../../../Components/Common/SurgeonSelector.svelte";
    import {
        CalendarDisplayEncounterTypes,
        CalendarIdleRefreshIntervalSeconds,
        DailyPlannerSingleEventMaxHeightPx,
    } from "../helpers/CalendarConstants.svelte";

    import { openViewEncounterSidePanel } from "../../PatientEncounter/helpers/encounterHelper.svelte";

    export let accountId = Number(0);
    export let params = {};
    export let url = "";

    const defaultHeaderWidth = 79;

    $: isSelectedToday = getYYYYMMDD(new Date()) == getYYYYMMDD(calendarDate);

    $: displayDoctors = filterDisplayDoctors(doctors, searchAccountText);

    $: selectAllCheckboxChecked =
        doctors.length === displayDoctors.length && displayDoctors.length;

    $: isUpdateEventTimeButtonDisabled =
        !editRepeatingEventType &&
        ((resizingEvent &&
            resizingEvent.calendarEvent?.isRepeating &&
            resizingEvent.calendarEvent?.id !==
                resizingEvent.calendarEvent?.parentId) ||
            (draggingEvent &&
                draggingEvent.calendarEvent?.isRepeating &&
                draggingEvent.calendarEvent?.id !==
                    draggingEvent.calendarEvent?.parentId));

    $: isBrowserFirefox = navigator.userAgent.includes("Firefox");

    $: checkStopIdleRefreshTimer =
        (selectedEncounterProps && !isObjectEmpty(selectedEncounterProps)) ||
        isCreateEventModalOpen ||
        isUpdateEventTimeModalOpen ||
        isEventNestedModalOpen ||
        isModifyEventModalOpen ||
        isCalendarRecurrenceModalOpen ||
        isCreateNewSurgeonModalOpen ||
        draggingEvent ||
        resizingEvent;

    $: if (checkStopIdleRefreshTimer) clearIdleRefreshTimer();
    else startIdleRefreshTimer();

    let startX = 0,
        mouseOffsetX = 0,
        mouseOffsetY = 0,
        moveType;

    let draggingEvent = null,
        dragPreviewData = null;

    let resizingEvent,
        isResizingStart = false;

    let isSelectingArea = false,
        selectionData = {};

    let isLoadingData = false,
        isLoadingCalendarData = false;

    let doctors = [],
        doctorIds = [],
        returnedEncounters = [],
        returnedEvents = [];

    let isCreateEventModalOpen = false,
        isCreatingEvent = false,
        createEventProps = {},
        isCalendarRecurrenceModalOpen = false;

    let isUpdateEventTimeModalOpen = false,
        isUpdatingEvent = false;

    let selectedEncounterProps = {};

    let calendarDate = new Date();

    let tooltipData = {},
        showTooltip = false,
        tooltipPosition = { top: 0, left: 0, bottom: null, right: null };

    let searchAccountText = "",
        isSearchingAccount = false;

    let editEventObj = {},
        isModifyEventModalOpen = false;

    let columnWidth = 0;
    const TIME_BLOCK = 15;
    const TIME_BLOCKS_PER_HOUR = 60 / TIME_BLOCK;

    let filters = { jumpDate: new Date(), billingStatus: "0,1,2,3,4,5" },
        selectedAccountDoctors = [],
        skipEncounterLoading,
        showFullDoctorName = true,
        editRepeatingEventType,
        skipEventsLoading;

    let isFetchedHospital = false,
        isFetchedSurgeon = false;

    let idleRefreshTimer;

    const dispatch = createEventDispatcher();

    onMount(async () => {
        clearIdleRefreshTimer();

        $dailyPlannerHourLabelWidth = defaultHeaderWidth;

        filters.showEncountersOnDailyPlanner =
            $user?.settings?.showEncountersOnDailyPlanner;

        if (params.query) await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        isLoadingData = true;

        await loadDoctors();

        if (doctorIds.length) await loadCalendarData();

        isLoadingData = false;

        setTimeout(() => initTable(), 0);

        window.addEventListener("resize", checkHourlyHeaderWidth);
    });

    onDestroy(() => {
        clearIdleRefreshTimer();
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = query.split("&");

        if (!arr.length) return;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i].includes("jumpDate=")) {
                calendarDate = new Date(`${arr[i].split("=")[1]}T00:00:00`);
            }

            if (arr[i].includes("searchDoctorText="))
                searchAccountText = arr[i].split("=")[1].toString();

            if (arr[i].includes("billingStatus="))
                filters.billingStatus = isNaN(Number(arr[i].split("=")[1]))
                    ? "0,1,2,3,4,5"
                    : Number(arr[i].split("=")[1]);

            if (arr[i].includes("surgeonIDs="))
                selectedAccountDoctors = arr[i].split("=")[1].split(",");

            if (arr[i].includes("eventType=")) {
                filters.eventType = Number(arr[i].split("=")[1]);
            }

            if (arr[i].includes("eventCategory=")) {
                filters.eventCategory = Number(arr[i].split("=")[1]);
            }

            // if (arr[i].includes("showEncounters=")) {
            //     filters.showEncounters = arr[i].split("=")[1];
            // }
        }
    };

    const handleFilterSelect = async () => {
        updateURL();

        if (accountId || doctors.length) loadCalendarData();
    };

    const handleSelectSurgeonFromSearch = (searchResult) => {
        selectedAccountDoctors = [...searchResult];

        handleFilterSelect();
    };

    const handleEventTypeChange = () => {
        if (filters.eventType === "encounter") {
            filters.showEncountersOnDailyPlanner = true;
        }
        handleFilterSelect();
    };

    const handleSelectDate = () => {
        calendarDate = new Date(`${calendarDate}T00:00:00`);
        loadCalendarData();
    };

    const handleDateChange = (day = 0) => {
        calendarDate = day
            ? new Date(`${getDaysDifferFrom(day, calendarDate)}T00:00:00`)
            : new Date(`${getYYYYMMDD(new Date())}T00:00:00`);
        loadCalendarData();
    };

    const updateURL = () => {
        replaceURL(`${url}${_getParamsString()}`);
    };

    const initTable = () => {
        const scrollContainer = document.querySelector(".scroll-container");
        const scrollToColumn = 8 * 4; // scroll to 8AM
        columnWidth = scrollContainer.scrollWidth / (24 * 4);
        scrollContainer.scrollLeft = scrollToColumn * columnWidth;

        const encounterContainer =
            document.getElementById("encounterContainer");
        const doctorColumn = document.getElementById("doctorColumn");

        encounterContainer.addEventListener("scroll", () => {
            doctorColumn.scrollTop = encounterContainer.scrollTop;
        });
        doctorColumn.addEventListener("scroll", () => {
            encounterContainer.scrollTop = doctorColumn.scrollTop;
        });
        checkHourlyHeaderWidth();
    };

    const checkHourlyHeaderWidth = () => {
        const encounterContainer =
            document.getElementById("encounterContainer");
        if (encounterContainer && encounterContainer.clientWidth)
            $dailyPlannerHourLabelWidth = Math.max(
                Math.floor(
                    (encounterContainer.clientWidth -
                        (isBrowserFirefox ? 0 : 10)) /
                        24,
                ),
                defaultHeaderWidth,
            );
    };

    const loadDoctors = async () => {
        if (accountId)
            await apiMethods.getMethod
                .getAccountByUserId($user.id)
                .then((res) =>
                    _handleLoadDoctors({
                        results: [{ ...res }],
                        error: res.error ?? "",
                    }),
                )
                .catch((err) => showError(err));
        else {
            await cacheAccountPrePopulateSearchOptions();

            _handleLoadDoctors({
                results: $prePopulateAccountOptions,
            });
        }
    };

    const loadCalendarData = async (id, doctorId) => {
        isLoadingCalendarData = true;

        skipEventsLoading = false;

        if (filters.eventType === "encounter") skipEventsLoading = true;

        await loadEncounters();

        await loadEvents();

        await mappingDataToDoctors();

        updateURL();

        resetEventValue();

        startIdleRefreshTimer();

        isLoadingCalendarData = false;

        if (id && doctorId) {
            const doctorIndex = doctors.findIndex((d) => d.id === doctorId);

            const targetDoctor = displayDoctors[doctorIndex];

            if (!targetDoctor) return;
            Object.values(targetDoctor.eventsMap).forEach((ov) => {
                if (ov?.listedEvent?.length) {
                    ov.listedEvent.forEach((ev) => {
                        if (ev[0].id === id) {
                            handleClickEvent(ev[0], doctorIndex);
                        }
                    });
                }
            });
            isReloadingEvent = false;
        }
    };

    const loadEncounters = async () => {
        if (skipEncounterLoading || (!accountId && !doctorIds.length))
            return (returnedEncounters = []);

        const filterDate = getDateFromDateString(calendarDate)
            .split(", ")[0]
            .split("/")
            .reverse()
            .join("-");

        const encounterFilters = {
            sort: "scheduleddate",
            "filter[simplified]": 1,
            "filter[status]": CalendarDisplayEncounterTypes.join(),
            "filter[scheduledDateFrom]": filterDate,
            "filter[scheduledDateTo]": filterDate,
        };

        if (accountId) encounterFilters["filter[doctorId]"] = Number(accountId);
        else if (doctorIds.length && doctorIds.length !== doctors.length)
            encounterFilters["filter[doctorId]"] = doctorIds.join();

        if (filters.billingStatus || filters.billingStatus == 0)
            encounterFilters["filter[billingStatus]"] = filters.billingStatus;

        if (selectedAccountDoctors.length)
            encounterFilters["filter[surgeonUniqueID]"] =
                selectedAccountDoctors.join(",");

        await apiMethods.getMethod
            .getPatientEncounter(0, 999999, encounterFilters)
            .then((res) => _handleLoadEncounter(res))
            .catch((err) => showError(err));
    };

    const loadEvents = async () => {
        if (skipEventsLoading || (!accountId && !doctorIds.length))
            return (returnedEvents = []);

        const filterDate = getDateFromDateString(calendarDate)
            .split(", ")[0]
            .split("/")
            .reverse()
            .join("-");

        const eventFilters = {
            "filter[fromDateFrom]": filterDate,
            "filter[toDateTo]": filterDate,
        };

        if (accountId) eventFilters["filter[doctorId]"] = Number(accountId);
        else if (doctorIds.length && doctorIds.length !== doctors.length)
            eventFilters["filter[doctorId]"] = doctorIds.join();

        if (filters.eventType || filters.eventType === 0)
            eventFilters["filter[eventtype]"] = filters.eventType;

        if (filters.eventCategory)
            eventFilters["filter[eventCategory]"] = filters.eventCategory;

        if (selectedAccountDoctors.length)
            eventFilters["filter[surgeonUniqueID]"] =
                selectedAccountDoctors.join(",");

        await apiMethods.getMethod
            .getAccountCalendarEvents(0, 999999, eventFilters)
            .then((res) => _handleLoadEvents(res))
            .catch((err) => showError(err));
    };

    const mappingDataToDoctors = async () => {
        doctors = doctors.map((doctor) => {
            doctor = {
                ...doctor,
                lineHeight: 0,
                encounters: [],
                listedEncounter: [],
                encounterRowHeight: [],
                eventsMap: {},
                selected: true,
            };

            return mapAndMergeEncountersAndEvents(
                doctor,
                returnedEncounters,
                returnedEvents,
                filters.showEncountersOnDailyPlanner,
            );
        });
    };

    const updateSelectedEvent = async () => {
        if (moveType && (moveType == "drag" || moveType == "resize"))
            await updateEvent();
    };

    const updateEvent = async () => {
        isUpdatingEvent = true;

        const tempEvent = moveType == "drag" ? draggingEvent : resizingEvent;

        const isEncounter = !tempEvent.hasOwnProperty("eventType");

        if (isEncounter) {
            tempEvent.calendarEvent.dischargeDate =
                getEncounterDischargeDateWithFallBackDuration(
                    tempEvent.calendarEvent,
                );

            // no need to consider discharge date as this move includes start &  end Date
            const newValues = {
                scheduledDate: `${tempEvent.calendarEvent.scheduledDate.split("T")[0]}T${getFormatHHMM(tempEvent.newStart)}:00`,
                dischargeDate: `${tempEvent.calendarEvent.dischargeDate.split("T")[0]}T${getFormatHHMM(tempEvent.newEnd)}:00`,
            };

            if (tempEvent.hasDoctorReassigned) {
                newValues.doctor = displayDoctors[tempEvent.newDoctorIndex];
                await loadAndUpdatePatientEncounterFields(
                    tempEvent.calendarEvent.id,
                    newValues,
                );
            } else {
                await apiMethods.putMethod
                    .updateEncounterScheduleDate(
                        tempEvent.calendarEvent.id,
                        newValues,
                    )
                    .catch((err) => showError(err));
            }
        } else {
            if (tempEvent.calendarEvent.isRepeating)
                switch (editRepeatingEventType) {
                    case EditRepeatingEventOptions.thisOnly:
                        tempEvent.calendarEvent = resetCalendarRecurrenceEvent(
                            tempEvent.calendarEvent,
                        );
                        break;
                    case EditRepeatingEventOptions.thisAndFollowing:
                        tempEvent.calendarEvent.parentId =
                            tempEvent.calendarEvent.id;
                        break;
                    default:
                        break;
                }

            // updateNestedEncountersDate if dragging event
            if (
                tempEvent.calendarEvent.nestedEncounter?.length &&
                moveType === "drag"
            ) {
                const allCalls = [];
                const prevStartDate = tempEvent.calendarEvent.fromDate.includes(
                    "T",
                )
                    ? tempEvent.calendarEvent.fromDate
                    : `${tempEvent.calendarEvent.fromDate}T${getFormatHHMM(tempEvent.newStart)}:00`;
                const newStartDate = `${tempEvent.calendarEvent.fromDate.split("T")[0]}T${getFormatHHMM(tempEvent.newStart)}:00`;
                const timeDiff = getTimeDiffInMillSec(
                    newStartDate,
                    prevStartDate,
                );

                if (timeDiff !== 0) {
                    tempEvent.calendarEvent.nestedEncounter.forEach(
                        (encounter) => {
                            const newDate = getTimeAfterChanges(
                                encounter.scheduledDate,
                                timeDiff,
                            );
                            const dischargeDate = encounter.dischargeDate
                                ? getTimeAfterChanges(
                                      getEncounterDischargeDateWithFallBackDuration(
                                          encounter,
                                      ),
                                      timeDiff,
                                  )
                                : null;
                            allCalls.push(
                                apiMethods.putMethod
                                    .updateEncounterScheduleDate(encounter.id, {
                                        scheduledDate: newDate,
                                        dischargeDate,
                                    })
                                    .then((res) => {
                                        if (!res || res.error || !res.id) {
                                            const err =
                                                res.error ??
                                                "Failed to update encounter date/time, please refresh the page and try again and contact support if the issue persists.";
                                            throw new Error(err);
                                        }
                                    })
                                    .catch(() => {
                                        showError(
                                            "Failed to update some encounters, please refresh the page and try again and contact support if the issue persists.",
                                        );
                                    }),
                            );
                        },
                    );
                    await Promise.all(allCalls);
                }
            }

            const updatedFields = {
                fromDate: `${tempEvent.calendarEvent.fromDate.split("T")[0]}T${getFormatHHMM(tempEvent.newStart)}:00`,
                toDate: `${tempEvent.calendarEvent.toDate.split("T")[0]}T${getFormatHHMM(tempEvent.newEnd)}:00`,
            };

            if (tempEvent.hasDoctorReassigned)
                updatedFields.doctorId =
                    displayDoctors[tempEvent.newDoctorIndex].id;

            await updateAccountCalendarEvent(
                tempEvent.calendarEvent.id,
                updatedFields,
            );
        }

        await loadCalendarData();

        isUpdatingEvent = false;

        closeModifyEventModal();
    };

    const createEvent = async () => {
        if (createEventProps.eventType == "encounter") {
            selectedEncounterProps = {
                startDate: getYYYYMMDD(calendarDate).split("T")[0],
                endDate: getYYYYMMDD(calendarDate).split("T")[0],
                startTime: createEventProps.start,
                endTime: createEventProps.end,
                doctorID: createEventProps.doctor.id,
                afterSaveEncounter: () => closeExpandSidePanel(true),
            };
            isCreateEventModalOpen = false;
        } else {
            const eventTypeMap = {
                leave: Number(0),
                list: Number(1),
                onCall: Number(2),
            };

            let eventObj = {
                ...createEventProps,
                doctorId: createEventProps.doctor.id,
                eventType: eventTypeMap[createEventProps.eventType],
                fromDate: `${getYYYYMMDD(calendarDate).split("T")[0]}T${getFormatHHMM(createEventProps.start)}:00`,
                toDate: `${getYYYYMMDD(calendarDate).split("T")[0]}T${getFormatHHMM(createEventProps.end)}:00`,
            };

            if (
                eventObj.eventType == 0 ||
                (eventObj.hasOwnProperty("isRepeating") &&
                    !eventObj.isRepeating)
            )
                eventObj = resetCalendarRecurrenceEvent(eventObj);

            if (eventObj.eventType === 1) {
                if (selectedHospital?.id)
                    eventObj.hospitalId = selectedHospital.id;

                if (selectedSurgeon?.id)
                    eventObj.surgeonId = selectedSurgeon.id;
            }

            isCreatingEvent = true;

            await apiMethods.postMethod.createAccountCalendarEvent(eventObj);

            await loadEvents();

            mappingDataToDoctors();

            isCreatingEvent = false;
        }

        createEventProps.eventType != "encounter" && closeModifyEventModal();
    };

    const handleClickEvent = (calendarEvent, doctorIndex) => {
        if (moveType == "resize") return;

        const isEncounter = !calendarEvent.hasOwnProperty("eventType");

        if (isEncounter) {
            // openViewEncounterNewPage(calendarEvent.id);

            // $EncounterSidePanelProps.id = calendarEvent.id;
            openViewEncounterSidePanel({
                encounterId: info.event.id,
                onCloseFunction: () => loadCalendarData(),
            });
        }
        // selectedEncounterProps = selectedEncounterProps.hasOwnProperty("id")
        //     ? { id: calendarEvent.id, prevId: selectedEncounterProps.id }
        //     : { id: calendarEvent.id };
        else {
            const targetDoctor = displayDoctors[doctorIndex];

            editEventObj = {
                ...calendarEvent,
                ...syncCalendarEventDate(calendarEvent),
            };
            editEventObj.accountId = targetDoctor.id;

            if (editEventObj.eventType === 1)
                openListWithEncounterModal(editEventObj);
            else isModifyEventModalOpen = true;
        }
    };

    const handleDragStart = (event, calendarEvent, doctor, doctorIndex) => {
        if (!moveType || moveType != "drag") return;

        draggingEvent = { calendarEvent, doctor, doctorIndex };

        if (calendarEvent.hasOwnProperty("eventType"))
            draggingEvent.eventType = calendarEvent.eventType;

        startX = event.clientX;
        mouseOffsetX = event.offsetX;
        mouseOffsetY = event.offsetY;
        dragPreviewData = null;

        initDragPreview(event, doctorIndex);

        hideEncounterTooltip();
    };

    const handleDragEnter = (event) => event.preventDefault();

    const handleDragOver = (event, doctorIndex) => {
        event.preventDefault();

        updateDragPreview(event, doctorIndex);
    };

    const handleDragEnd = () => {
        if (!moveType || moveType != "drag") return;

        startX = 0;
    };

    const initDragPreview = (event, doctorIndex) => {
        if (!moveType || moveType != "drag") return;

        if (!draggingEvent) {
            dragPreviewData = null;
            return;
        }

        const timeSlot = event.currentTarget;
        const boundingRect = timeSlot.getBoundingClientRect();

        const isDraggingEvent = draggingEvent.hasOwnProperty("eventType");

        // get new left position
        const dropPosition =
            ((event.clientX - boundingRect.left) / boundingRect.width) * 100;
        const totalMinutes = (dropPosition / 100) * 24 * 60;
        const dropHour = Math.floor(totalMinutes / 60);
        const dropMinutes = Math.floor(totalMinutes % 60);
        const dropTime = `${String(dropHour).padStart(2, "0")}:${String(dropMinutes).padStart(2, "0")}`;
        const left = calculatePosition(dropTime);

        // get encounter width
        const startPercent = calculatePosition(
            draggingEvent.calendarEvent.start,
        );
        const endPercent = calculatePosition(draggingEvent.calendarEvent.end);
        const width = endPercent - startPercent;

        const dropVerticalPosition = isDraggingEvent
            ? draggingEvent.calendarEvent.relativeHeight
            : timeSlot.offsetTop;

        // remain dragging mouse position by subtracting offsetX
        const offsetLeft = (mouseOffsetX / boundingRect.width) * 100;

        dragPreviewData = {
            left: `${left - offsetLeft}%`,
            top: `${dropVerticalPosition}px`,
            initTop: `${dropVerticalPosition}px`,
            width: `${width}%`,
            doctorIndex,
        };
    };

    const updateDragPreview = (event, doctorIndex) => {
        const target = event.currentTarget;
        const { left, width } = target.getBoundingClientRect();
        const mouseX = event.clientX - left - mouseOffsetX;

        const timeBlocks = Math.round(
            (mouseX / width) * TIME_BLOCKS_PER_HOUR * 24,
        );

        const newLeft = (timeBlocks / (TIME_BLOCKS_PER_HOUR * 24)) * 100;

        if (draggingEvent && dragPreviewData) {
            dragPreviewData.left = `calc(${newLeft}% - 5px)`;
            // only show doctor change when moving encounter
            if (!draggingEvent.hasOwnProperty("eventType")) {
                draggingEvent.hasDoctorReassigned =
                    displayDoctors[doctorIndex].id !=
                    draggingEvent.calendarEvent?.doctorId;
                draggingEvent.newDoctorIndex = doctorIndex;
                dragPreviewData.doctorIndex = doctorIndex;
            }

            const newTimeStartBlocks = Math.round(mouseX / columnWidth);

            draggingEvent.newStart =
                calculateNewTimeByTimeBlocks(newTimeStartBlocks);

            const totalTimeBlocks =
                getTimeBlocksByTime(draggingEvent.calendarEvent.end) -
                getTimeBlocksByTime(draggingEvent.calendarEvent.start);

            const endTimeBlocks = newTimeStartBlocks + totalTimeBlocks;

            draggingEvent.newEnd = calculateNewTimeByTimeBlocks(endTimeBlocks);

            dragPreviewData.top = draggingEvent.hasDoctorReassigned
                ? `${0}%`
                : `${dragPreviewData.initTop}`;
        }
    };

    const handleDrop = (event, doctorIndex) => {
        if (!moveType || !draggingEvent) return;

        event.preventDefault();
        if (
            draggingEvent.calendarEvent &&
            draggingEvent.calendarEvent.fromDate &&
            draggingEvent.calendarEvent.toDate &&
            draggingEvent.calendarEvent.fromDate.substring(11, 16) ===
                draggingEvent.newStart &&
            draggingEvent.calendarEvent.toDate.substring(11, 16) ===
                draggingEvent.newEnd
        )
            return resetEventValue();

        openUpdateEventModal();
    };

    const handleMouseDown = (event, calendarEvent, doctor, doctorIndex) => {
        // avoid interact with encounters/lists trigger selectArea
        if (moveType) return;

        if (!calendarEvent) {
            // select area
            moveType = "selectArea";
            isSelectingArea = true;
            selectionData.selectionStart = event.clientX;
            selectionData.selectionEnd = event.clientX;
            updateSelection(event, doctorIndex);
        } else {
            // either dragNdrop or resize
            const boundingRect = event.target.getBoundingClientRect();
            startX = event.clientX;
            const onEdge =
                Math.abs(startX - boundingRect.left) <=
                    DailyPlannerEventResizeBorderWidth ||
                Math.abs(startX - boundingRect.right) <=
                    DailyPlannerEventResizeBorderWidth;

            moveType = onEdge ? "resize" : "drag";

            event.target.draggable = !onEdge;

            if (onEdge) {
                resizingEvent = { calendarEvent, doctor, doctorIndex };

                const isResizingEvent =
                    calendarEvent.hasOwnProperty("eventType");

                if (isResizingEvent)
                    resizingEvent.eventType = calendarEvent.eventType;

                const dropVerticalPosition = isResizingEvent
                    ? calendarEvent.relativeHeight
                    : event.target.offsetTop;

                resizingEvent.originalStart = calendarEvent.start;
                resizingEvent.originalEnd = calendarEvent.end;
                isResizingStart = Math.abs(startX - boundingRect.left) <= 10;

                dragPreviewData = {
                    initTop: `${dropVerticalPosition}px`,
                    top: `${dropVerticalPosition}px`,
                };

                draggingEvent = null;
            }
        }
    };

    const handleMouseMove = (event, doctorIndex) => {
        event.preventDefault();

        if (!moveType) return;

        if (moveType == "resize") {
            const {
                resizingEvent: newResizingEvent,
                dragPreviewData: newDragPreviewData,
            } = handleResize(
                event,
                doctorIndex,
                resizingEvent,
                isResizingStart,
            );

            resizingEvent = { ...newResizingEvent };
            dragPreviewData = {
                ...dragPreviewData,
                ...newDragPreviewData,
                top: resizingEvent.hasDoctorReassigned
                    ? `${0}%`
                    : `${dragPreviewData.initTop}`,
            };
        } else if (moveType == "selectArea" && isSelectingArea) {
            selectionData.selectionEnd = event.clientX;
            updateSelection(event, doctorIndex);
        } else if (moveType == "drag") updateDragPreview(event, doctorIndex);
    };

    const handleMouseUp = (event, doctorIndex) => {
        event.preventDefault();

        startX = 0;
        mouseOffsetX = 0;
        mouseOffsetY = 0;
        isResizingStart = false;

        if (moveType == "resize") {
            if (
                resizingEvent.originalStart === resizingEvent.start &&
                resizingEvent.originalEnd === resizingEvent.end
            )
                return resetEventValue();
            resizingEvent.newStart = resizingEvent.start;
            resizingEvent.newEnd = resizingEvent.end;
            event.target.draggable = true;
            openUpdateEventModal();
        } else if (isSelectingArea) {
            isSelectingArea = false;
            createEventProps.start = calculateTimeFromPosition(
                selectionData.left,
            );
            createEventProps.end = calculateTimeFromPosition(
                selectionData.left + selectionData.width,
            );
            createEventProps.doctor = displayDoctors[doctorIndex];
            createEventProps.eventType = "list";
            isCreateEventModalOpen = true;
        }
    };

    const updateSelection = (event, doctorIndex) => {
        selectionData = handleSelectArea(
            event,
            doctorIndex,
            selectionData,
            TIME_BLOCKS_PER_HOUR,
        );
    };

    const handleToggleRecurrenceModalOpen = (eventObj = {}) => {
        if (!eventObj.isRepeating) return;

        isCalendarRecurrenceModalOpen = true;
    };

    const openUpdateEventModal = () => (isUpdateEventTimeModalOpen = true);

    const closeModifyEventModal = () => {
        isCreateEventModalOpen = false;
        isUpdateEventTimeModalOpen = false;
        isModifyEventModalOpen = false;
        editRepeatingEventType = undefined;

        isFetchedHospital = false;
        isFetchedSurgeon = false;

        if (!isEventNestedModalOpen) resetEventValue();
    };

    const resetEventValue = () => {
        moveType = null;

        draggingEvent = null;
        dragPreviewData = null;

        resizingEvent = null;
        isResizingStart = false;

        isSelectingArea = false;
        selectionData = {};

        createEventProps = {};

        selectedEncounterProps = {};

        editEventObj = {};

        hideEncounterTooltip();
        accountDoctors = [];
        selectedHospital = undefined;
        selectedSurgeon = undefined;
    };

    const closeExpandSidePanel = async (requireReloadEncounter = false) => {
        resetEventValue();

        if (requireReloadEncounter) {
            await loadEncounters();

            mappingDataToDoctors();
        }
    };

    const showEncounterTooltip = (event, encounter) => {
        if (moveType) return;

        tooltipData = { ...encounter };

        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const tooltipWidth = 500;
        const tooltipHeight = 300;

        let top = event.clientY + 10;
        let left = event.clientX + 10;
        let bottom = null;
        let right = null;

        // Adjust if the tooltip goes beyond the right edge of the screen
        if (event.clientX + tooltipWidth + 10 > screenWidth) {
            left = null;
            right = screenWidth - event.clientX + 10;
        }

        // Adjust if the tooltip goes beyond the bottom edge of the screen
        if (event.clientY + tooltipHeight + 10 > screenHeight) {
            top = null;
            bottom = screenHeight - event.clientY + 10;
        }

        // Adjust if the tooltip goes beyond the left edge of the screen
        if (event.clientX - tooltipWidth - 10 < 0) {
            left = event.clientX + 10;
            right = null;
        }

        // Adjust if the tooltip goes beyond the top edge of the screen
        if (event.clientY - tooltipHeight - 10 < 0) {
            top = event.clientY + 10;
            bottom = null;
        }

        tooltipPosition = { top, left, bottom, right };

        showTooltip = true;
    };

    const hideEncounterTooltip = () => {
        tooltipData = {};
        tooltipPosition = { top: 0, left: 0, bottom: null, right: null };
        showTooltip = false;
    };

    const filterDisplayDoctors = (allDoctors, keyword) => {
        checkHourlyHeaderWidthDelayed();
        if (!allDoctors || !allDoctors.length) return [];

        // pre-filter out pre-selected doctors before filtering by keyword
        if (filters.doctorId) {
            doctorIds = filters.doctorId.split(",").map((id) => Number(id));

            allDoctors = allDoctors.filter((doctor) =>
                doctorIds.includes(doctor.id),
            );
        }

        if (keyword) {
            return allDoctors.filter(
                (doctor) =>
                    doctor.name.toUpperCase().includes(keyword.toUpperCase()) ||
                    doctor.id == keyword,
            );
        }

        return allDoctors.filter((doctor) => doctor.selected);
    };

    // HELPER FUNCTIONS
    const _handleLoadDoctors = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load doctor details, please refresh the page and try again and contact support if the issue persists.",
            );
        else
            res.results.map((d) => {
                doctors.push({
                    id: d.id,
                    encounters: [],
                    list: [],
                    name: `${d.titleName} ${d.fullName}`,
                    shortName: getShortDoctorName(d),
                    calendarColour: defaultCalendarEventColor,
                });
                doctorIds.push(d.id);
            });
    };

    const _handleLoadEncounter = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load encounters, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            returnedEncounters = res.results.map((encounter) => ({
                ...encounter,
                fromDate: encounter.scheduledDate,
                toDate: encounter.dischargeDate,
            }));
        }
    };

    const _handleLoadEvents = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load events, please refresh the page and try again and contact support if the issue persists.",
            );
        else returnedEvents = [...res.results];
    };

    const _getParamsString = () => {
        let str = "";

        if (!accountId) str += "/";

        if (calendarDate) str += `&jumpDate=${getYYYYMMDD(calendarDate)}`;

        if (searchAccountText) str += `&searchDoctorText=${searchAccountText}`;

        if (filters.eventType) str += `&eventType=${filters.eventType}`;

        if (filters.eventCategory)
            str += `&eventCategory=${filters.eventCategory}`;

        // if (filters.showEncounters)
        //     str += `&showEncounters=${filters.showEncounters}`;

        if (
            (filters.billingStatus || filters.billingStatus === 0) &&
            filters.billingStatus !== "0,1,2,3,4"
        )
            str += `&billingStatus=${filters.billingStatus}`;

        if (selectedAccountDoctors.length)
            str += `&surgeonIDs=${selectedAccountDoctors.join(",")}`;

        return str;
    };

    const toggleDoctorSelected = (doctor) => {
        if (accountId) return;
        doctor.selected = !doctor.selected;
        setTimeout(() => {
            doctors = [...doctors];
        }, 100);
    };

    const onSelectAllCheckboxClick = () => {
        if (accountId) return;
        if (doctors.length === displayDoctors.length) {
            doctors.forEach((doc) => (doc.selected = false));
        } else {
            doctors.forEach((doc) => (doc.selected = true));
        }

        doctors = [...doctors];
    };

    let selectedHospital,
        selectedSurgeon,
        surgeonOptions = [],
        hospitalOptions = [],
        suggestedAccountDoctors = [],
        suggestedHospitals = [],
        accountDoctors = [],
        isCreateNewSurgeonModalOpen = false,
        isLoadingSuggestedAccountDoctors = false,
        isLoadingSuggestedHospitals = false;

    $: _syncSurgeonOptions(accountDoctors, suggestedAccountDoctors);

    const loadHospitals = async (searchText) => {
        const res = searchText
            ? await apiMethods.getMethod.getHospitals(0, 999999, {
                  search: searchText,
              })
            : [];

        return _handleSearchHospitals(
            res.results?.length > 0 ? res.results : [],
        );
    };

    const _handleSearchHospitals = (res = []) => {
        hospitalOptions = [];

        if (!res.length && !suggestedHospitals.length) return [];

        const suggestedOptions = [];

        if (res.length) {
            hospitalOptions = [...res];
            return [...res];
        }

        if (!suggestedHospitals.length) return [];

        suggestedHospitals.map((hospital) => {
            suggestedOptions.push({
                ...hospital,
                groupName: "Suggested Hospitals",
            });
        });

        hospitalOptions = [...suggestedOptions];

        return [...suggestedOptions];
    };

    const groupBy = (option) => option.groupName;

    const loadSuggestedAccountDoctor = async () => {
        if (isFetchedSurgeon) return;

        const doctorID = currentEventDoctorId;

        if (!doctorID) return;

        isLoadingSuggestedAccountDoctors = true;

        await apiMethods.getMethod.getSuggestedSurgeon(doctorID).then((res) => {
            if (res.results) suggestedAccountDoctors = [...res.results];
        });

        //filter surgeons and take only once by unique id
        suggestedAccountDoctors = [
            ...new Map(suggestedAccountDoctors.map(doc => [doc.providerUniqueId, doc])).values()
        ];

        if (suggestedAccountDoctors.length > 5)
            suggestedAccountDoctors = suggestedAccountDoctors.slice(0, 5);

        await loadAccountDoctors(doctorID);

        isFetchedSurgeon = true;

        isLoadingSuggestedAccountDoctors = false;
    };

    $: currentEventDoctorId = accountId
        ? accountId
        : createEventProps?.doctor?.id;

    const mapSelectionFromEventData = () => {
        if (createEventProps.hospitalId && createEventProps.hospitalName) {
            selectedHospital = {
                id: createEventProps.hospitalId,
                name: createEventProps.hospitalName,
            };
        } else {
            selectedHospital = null;
        }
        if (createEventProps.surgeonId && createEventProps.surgeonName) {
            selectedSurgeon = {
                id: createEventProps.surgeonId,
                displayLabel: createEventProps.surgeonName,
            };
        } else {
            selectedSurgeon = null;
        }
    };

    const loadSuggestedHospitals = async () => {
        if (isFetchedHospital) return;

        const doctorID = currentEventDoctorId;

        if (!doctorID) return;

        isLoadingSuggestedHospitals = true;

        await apiMethods.getMethod
            .getSuggestedHospital(doctorID)
            .then((res) => {
                if (res.results) {
                    suggestedHospitals = [...res.results];
                    _handleSearchHospitals([]);
                }
            });

        isFetchedHospital = true;

        isLoadingSuggestedHospitals = false;
    };

    const loadAccountDoctors = async (id) => {
        await apiMethods.getMethod
            .getAccountDoctors(0, 999999, {
                "filter[doctorId]": id,
            })
            .then((res) => {
                if (res.results) accountDoctors = [...res.results];
                else accountDoctors = [];
            });
    };

    const _syncSurgeonOptions = (
        accountDoctors = [],
        suggestedSurgeons = [],
    ) => {
        surgeonOptions = [];
        let uniqueAccountDoctors = [
            ...new Map(accountDoctors.map(doc => [doc.providerUniqueId, doc])).values()
        ];

        if (!suggestedSurgeons)
            uniqueAccountDoctors.map((doctor) => {
                surgeonOptions.push({
                    ...doctor,
                    displayLabel: getOptionLabelForSurgeon(doctor),
                });
            });
        else {
            const suggestedSurgeonUniqueIds = [];
            const len = Math.min(suggestedSurgeons.length, 5);

            for (let i = 0; i < len; i++) {
                const surgeon = suggestedSurgeons[i];

                surgeonOptions.push({
                    ...surgeon,
                    groupName: "Suggested surgeons",
                    displayLabel: getOptionLabelForSurgeon(surgeon),
                });

                suggestedSurgeonUniqueIds.push(surgeon.providerUniqueId);
            }

            uniqueAccountDoctors.map((surgeon) => {
                if (
                    !suggestedSurgeonUniqueIds.includes(
                        surgeon.providerUniqueId.toString(),
                    )
                )
                    surgeonOptions.push({
                        ...surgeon,
                        groupName: "Other Surgeons",
                        displayLabel: getOptionLabelForSurgeon(surgeon),
                    });
            });
        }
    };

    const openCreateNewSurgeonModal = () => {
        isCreateNewSurgeonModalOpen = true;
    };

    const closeCreateNewSurgeonModal = () =>
        (isCreateNewSurgeonModalOpen = false);

    let isEventNestedModalOpen = false,
        isReloadingEvent = false;

    const reloadReOpenCurrentEventModal = async (event) => {
        if (!event?.id) return;

        const tempEventObj = { ...editEventObj };

        const { id, doctorId } = event;

        await loadCalendarData(id, doctorId);

        // Daily calendar only nest encounters into events, not encounters itself
        const targetDoctor = displayDoctors.find(
            (doctor) => doctor.id == doctorId,
        );

        const eventsMap = targetDoctor?.eventsMap;

        const _findEventById = (obj, eventId) => {
            for (let key in obj) {
                if (obj.hasOwnProperty(key)) {
                    const value = obj[key];

                    if (typeof value == "object" && value != null) {
                        const result = _findEventById(value, eventId);
                        if (result) return result;
                    }

                    if (value && value.id == eventId) return value;
                }
            }

            return null;
        };

        const targetEvent = _findEventById(eventsMap, tempEventObj.id);

        if (!targetEvent) {
            isEventNestedModalOpen = false;
            return;
        }

        editEventObj = {
            ...targetEvent,
            startDate: tempEventObj.startDate,
            endDate: tempEventObj.endDate,
        };
    };

    const handleEditEventDetails = () => {
        editEventObj = {
            ...editEventObj,
            isRepeating:
                (editEventObj.eventType == 1 || editEventObj.eventType == 2) &&
                editEventObj.repeatIntervalType > 0,
            isFullDay: editEventObj.allDay,
        };

        isModifyEventModalOpen = true;
    };

    const handleEditNestedEncounter = (encounter = {}, isDuplicate = false) => {
        if (isObjectEmpty(encounter)) return;

        selectedEncounterProps = { ...encounter, isDuplicate };
        closeNestedEncounterModal();
    };

    const closeNestedEncounterModal = () => {
        resetEventValue();
        isEventNestedModalOpen = false;
    };

    const openListWithEncounterModal = (eventObj) => {
        editEventObj = {
            ...eventObj,
            startDate: [eventObj.fromDate, `${eventObj.fromTime}:00`],
            endDate: [eventObj.toDate, `${eventObj.toTime}:00`],
            id: editEventObj.id,
        };

        isEventNestedModalOpen = true;
    };

    const onDoctorNameWidthToggle = () => {
        showFullDoctorName = !showFullDoctorName;
        checkHourlyHeaderWidthDelayed();
    };

    const checkHourlyHeaderWidthDelayed = debounce(
        () => checkHourlyHeaderWidth(),
        250,
    );

    const afterSubmit = async () => {
        if (
            isEventNestedModalOpen &&
            editEventObj.id &&
            editEventObj.doctorId &&
            editEventObj.eventType === DirectCalendarEventTypes.List
        ) {
            isEventNestedModalOpen = false;
            await loadCalendarData(editEventObj.id, editEventObj.doctorId);
        } else {
            await loadCalendarData();
            closeModifyEventModal();
        }
    };

    const startDuplicateEvent = () => {
        // eventType set as "list" because only list can show dup icon from daily planner.
        // used string value due to legacy issue
        createEventProps = {
            ...editEventObj,
            eventType: "list",
            start: editEventObj.startDate[1],
            end: editEventObj.endDate[1],
            doctor: {
                id: editEventObj.doctorId,
                name: editEventObj.doctorName,
            },
            eventCategory: editEventObj.eventCategory,
        };
        if (editEventObj.hospitalId && editEventObj.hospitalName)
            selectedHospital = {
                id: editEventObj.hospitalId,
                name: editEventObj.hospitalName,
            };

        if (editEventObj.surgeonId && editEventObj.surgeonName)
            selectedSurgeon = {
                id: editEventObj.surgeonId,
                name: editEventObj.surgeonName,
            };

        delete createEventProps.id;
        delete createEventProps.parentId;

        isEventNestedModalOpen = false;
        isCreateEventModalOpen = true;
    };

    const goToVView = () => {
        $user.settings.dailyPlannerVerticalView = true;
        dispatch("verticalView");
    };

    const clearIdleRefreshTimer = () => {
        window.clearInterval(idleRefreshTimer);
    };

    const startIdleRefreshTimer = () => {
        clearIdleRefreshTimer();
        idleRefreshTimer = setInterval(() => {
            loadCalendarData();
        }, CalendarIdleRefreshIntervalSeconds * 1000);
    };
</script>

<div
    class={`flex flex-col flex-1 gap-y-6 ${accountId ? "min-h-[300px]" : ""}`}
    style="--DailyPlannerEventResizeBorderWidth: {DailyPlannerEventResizeBorderWidth}px;"
>
    <div
        class={`flex w-full items-center gap-4 flex-wrap justify-start ${isObjectEmpty(selectedEncounterProps) ? "z-20" : "z-0"}`}
    >
        {#if !accountId}
            <DefaultDoctorFilter
                bind:filter={filters}
                filterPropertyName={"doctorId"}
                isLong
                onChangeFunc={() => {
                    displayDoctors = filterDisplayDoctors(
                        doctors,
                        searchAccountText,
                    );
                }}
            />

            <SearchInput
                bind:value={searchAccountText}
                title="Doctor"
                isLong
                inputClass="borderB"
            />
        {/if}

        <SearchInput
            bind:value={calendarDate}
            title="Date"
            type="date"
            isLong
            inputClass="borderB"
            inputFunc={handleSelectDate}
            showTimeInputClearButton={false}
            isDisable={isLoadingData}
            showScrollYearOptions
        />

        <SearchSelect
            title="Billing Status"
            isLong
            bind:value={filters.billingStatus}
            onChangeFunc={handleFilterSelect}
        >
            <option value="0,1,2,3,4" selected
                >All Statuses exclude complete</option
            >
            <option value="0,1,2,3,4,5">All Statuses</option>
            {#each BillingStatus as billingStatus}
                <option value={billingStatus.id}>{billingStatus.type}</option>
            {/each}
        </SearchSelect>

        <SurgeonSelector
            isDisabled={isLoadingData}
            bind:selectedSurgeonIds={selectedAccountDoctors}
            onChangeFunc={handleSelectSurgeonFromSearch}
            showCollapsedPlaceholder
            showSelected
            zIndex="100"
        />

        <SearchSelect
            title="Event Type"
            isLong
            bind:value={filters.eventType}
            onChangeFunc={handleEventTypeChange}
        >
            <option value={undefined} selected>All Types</option>
            {#each accountCalendarEventType as aceType}
                {#if aceType.doctorSpecific}
                    <option value={aceType.id}>{aceType.type}</option>
                {/if}
            {/each}
            <option value="encounter">Patient Encounter</option>
        </SearchSelect>

        <div class="flex flex-col">
            <p class="text-[11px] font-[500]">Event Category</p>
            <CalendarEventCategorySelector
                bind:value={filters.eventCategory}
                on:change={handleFilterSelect}
                emptyOptionName="All Categories"
                borderClass="border-bottom"
                widthClass="w-48"
            />
        </div>

        <div class="flex flex-col">
            <p class="text-[11px] font-[500]">Show Encounters</p>
            <input
                type="checkbox"
                class="toggle toggle-secondary"
                bind:checked={filters.showEncountersOnDailyPlanner}
                on:change={handleFilterSelect}
            />
        </div>

        {#if !accountId}
            <!-- doctor vertical view not enabled -->
            <div class="flex flex-col">
                <p class="text-[11px] font-[500]">Vertical View</p>
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    checked={false}
                    on:change={goToVView}
                />
            </div>
        {/if}

        <!-- to keep date mover section always stays at the right side of screen -->
        <div class="flex-1" />

        <ResetFilterLink
            bind:filter={filters}
            defaultFilter={{ billingStatus: "0,1,2,3,4,5" }}
            specialVariables={{
                selectedAccountDoctors,
            }}
            onResetFilter={() => {
                selectedAccountDoctors = [];
                handleFilterSelect();
            }}
        />

        <div class="flex w-fit border rounded rounded-lg mx-2 right-0">
            <button
                class="btn btn-ghost btn-outline btn-md text-[16px] font-normal normal-case rounded-r-none"
                on:click|preventDefault={() => handleDateChange(-1)}
            >
                Prev Day
            </button>
            <div class="divider divider-horizontal p-0" />
            <button
                class={`btn btn-ghost btn-outline btn-md text-[16px] font-normal normal-case rounded-none ${isSelectedToday ? "bg-gray-200 font-medium" : ""}`}
                on:click|preventDefault={() => handleDateChange()}
            >
                {getDisplayDate(calendarDate)}
            </button>
            <div class="divider divider-horizontal p-0" />
            <button
                class="btn btn-ghost btn-outline btn-md text-[16px] font-normal normal-case rounded-l-none"
                on:click|preventDefault={() => handleDateChange(1)}
            >
                Next Day
            </button>
        </div>
    </div>
    {#if isLoadingData || isSearchingAccount}
        <PageLoading />
    {:else}
        <div class="relative flex-1 z-10 rounded-xl">
            <div class="absolute inset-0 flex">
                <div
                    class="fixed-column rounded-l-xl {isBrowserFirefox
                        ? 'mb-[24px]'
                        : 'mb-[10px]'}"
                    id="doctorColumn"
                >
                    <div class="table-header doctor-name relative">
                        <div
                            class="flex flex-row flex-1 inset-0 justify-between items-center z-10"
                        >
                            <span
                                class="cursor-pointer {accountId ||
                                !showFullDoctorName
                                    ? ''
                                    : 'pl-9'}"
                                on:click={onSelectAllCheckboxClick}
                            >
                                {#if showFullDoctorName}
                                    Doctors
                                {:else}
                                    D
                                {/if}
                            </span>
                            <span
                                class="collapse-icon flex justify-end mx-3"
                                on:click={onDoctorNameWidthToggle}
                            >
                                <i
                                    style="font-size:24px"
                                    class={`fa cursor-pointer rounded-lg py-1 text-[#6F726C] ${
                                        showFullDoctorName
                                            ? "rotate-back"
                                            : "rotate-180"
                                    }`}
                                >
                                    &#xf104;
                                </i>
                            </span>
                        </div>
                        {#if !accountId && showFullDoctorName}
                            <input
                                type="checkbox"
                                id="selectAllCheckbox"
                                class="checkbox checkbox-sm checkbox-secondary mr-4 mt-1 z-0 absolute left-3"
                                checked={selectAllCheckboxChecked}
                            />
                        {/if}
                    </div>
                    <div class="flex flex-col">
                        {#each displayDoctors as doctor}
                            <div
                                class="table-cell doctor-name"
                                style="
                                    height: {`${doctor.lineHeight + 10}px`}
                                "
                            >
                                {#if !accountId && showFullDoctorName}
                                    <input
                                        type="checkbox"
                                        id={doctor.id}
                                        class="checkbox checkbox-sm checkbox-secondary mr-4 mt-1"
                                        bind:checked={doctor.selected}
                                        on:click|preventDefault={() =>
                                            toggleDoctorSelected(doctor)}
                                    />
                                {/if}
                                <label class="cursor-pointer" for={doctor.id}>
                                    {showFullDoctorName
                                        ? doctor.name
                                        : doctor.shortName}
                                </label>
                            </div>
                        {/each}
                    </div>
                </div>
                <div
                    class="scroll-container rounded-r-xl"
                    id="encounterContainer"
                >
                    {#each Array(24) as _, index}
                        <!-- added width to handle ultra wide or ultra small display -->
                        <div
                            class="table-header hour-table-header"
                            style="width: {$dailyPlannerHourLabelWidth}px;"
                        >
                            <div class="hour-label">
                                {formatTime(index)}
                            </div>
                        </div>
                    {/each}

                    {#each displayDoctors as doctor, doctorIndex}
                        <div
                            class="table-cell time-slot bg-[#fcf8e3]"
                            on:dragenter={handleDragEnter}
                            on:dragover={(event) =>
                                handleDragOver(event, doctorIndex)}
                            on:drop={(event) => handleDrop(event, doctorIndex)}
                            on:mousedown={(event) =>
                                handleMouseDown(
                                    event,
                                    null,
                                    doctor,
                                    doctorIndex,
                                )}
                            on:mousemove={(event) =>
                                handleMouseMove(event, doctorIndex)}
                            on:mouseup={(event) =>
                                handleMouseUp(event, doctorIndex)}
                            style="grid-column: span 48; position: relative; height: {`${doctor.lineHeight + 10}px`}"
                        >
                            {#each Array(48) as _, halfHour}
                                <div
                                    class="half-hour-line"
                                    style="left: {(halfHour *
                                        $dailyPlannerHourLabelWidth) /
                                        2}px;"
                                />
                            {/each}

                            {#if selectionData && selectionData.doctorIndex == doctorIndex}
                                <div
                                    class="selection"
                                    style="
                                left: calc({selectionData.left}% - 5px);
                                width: {selectionData.width}%;
                            "
                                ></div>
                            {/if}

                            {#if draggingEvent && dragPreviewData && dragPreviewData.doctorIndex == doctorIndex}
                                <div
                                    class="encounter flex flex-col items-start gap-y-1"
                                    style="
                                        left: {dragPreviewData.left};
                                        width: {dragPreviewData.width};
                                        top: {dragPreviewData.top};
                                        opacity: 0.5;
                                        position: absolute;
                                        z-index: 100;
                                        max-height: {DailyPlannerSingleEventMaxHeightPx}px;
                                        background-color: {draggingEvent
                                        .calendarEvent.eventColour};
                                        border: 1px solid {draggingEvent
                                        .calendarEvent.eventColour};
                                    "
                                >
                                    {#if !draggingEvent.hasOwnProperty("eventType")}
                                        <p
                                            class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {draggingEvent.calendarEvent
                                                .displayHospital}
                                        </p>
                                        <p
                                            class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {draggingEvent.calendarEvent
                                                .displaySurgeon}
                                        </p>
                                        <p
                                            class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {draggingEvent.newStart} -
                                            {draggingEvent.newEnd}
                                        </p>
                                    {:else}
                                        <!-- <p
                                            class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {draggingEvent.calendarEvent
                                                .displayDescription}
                                        </p>
                                        <p
                                            class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {draggingEvent.newStart} -
                                            {draggingEvent.newEnd}
                                        </p> -->
                                        <p
                                            class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                            style="color:{draggingEvent.fontColor ??
                                                '#FFF'}"
                                        >
                                            {draggingEvent.calendarEvent
                                                .displayCategory}
                                        </p>
                                        {#if draggingEvent.calendarEvent.displayHospital}
                                            <p
                                                class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                                style="color:{draggingEvent.fontColor ??
                                                    '#FFF'}"
                                            >
                                                {draggingEvent.calendarEvent
                                                    .displayHospital}
                                            </p>
                                        {/if}
                                        {#if draggingEvent.calendarEvent.displaySurgeon}
                                            <p
                                                class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                                style="color:{draggingEvent
                                                    .calendarEvent.fontColor ??
                                                    '#FFF'}"
                                            >
                                                {draggingEvent.calendarEvent
                                                    .displaySurgeon}
                                            </p>
                                        {/if}
                                        <p
                                            class={`${draggingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                            style="color:{draggingEvent
                                                .calendarEvent.fontColor ??
                                                '#FFF'}"
                                        >
                                            {draggingEvent.newStart} -
                                            {draggingEvent.newEnd}
                                        </p>
                                    {/if}
                                </div>
                            {:else if resizingEvent && dragPreviewData && dragPreviewData.doctorIndex == doctorIndex}
                                <div
                                    class="encounter noselect flex flex-col items-start gap-y-1"
                                    style="
                                        left: {dragPreviewData.left};
                                        width: {dragPreviewData.width};
                                        top: {dragPreviewData.top};
                                        opacity: 0.5;
                                        position: absolute;
                                        z-index: 100;
                                        background-color: {resizingEvent
                                        .calendarEvent.eventColour};
                                        border: 1px solid {resizingEvent
                                        .calendarEvent.eventColour};
                                        cursor: ew-resize;
                                    "
                                >
                                    {#if !resizingEvent.hasOwnProperty("eventType")}
                                        <p
                                            class={`${resizingEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {resizingEvent.calendarEvent
                                                .displayHospital}
                                        </p>
                                        <p
                                            class={`${resizingEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {resizingEvent.calendarEvent
                                                .displaySurgeon}
                                        </p>
                                        <p
                                            class={`${resizingEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {resizingEvent.start} -
                                            {resizingEvent.end}
                                        </p>
                                        <p
                                            class={`${resizingEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {resizingEvent.calendarEvent
                                                .procedureName}
                                        </p>
                                    {:else}
                                        <!-- <p
                                            class={`${resizingEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {resizingEvent.calendarEvent
                                                .displayDescription}
                                        </p>
                                        <p
                                            class={`${resizingEvent.hideDisplayText ? "invisible" : ""}`}
                                        >
                                            {resizingEvent.start} -
                                            {resizingEvent.end}
                                        </p> -->
                                        <p
                                            class={`${resizingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                            style="color:{resizingEvent.fontColor ??
                                                '#FFF'}"
                                        >
                                            {resizingEvent.calendarEvent
                                                .displayCategory}
                                        </p>
                                        {#if resizingEvent.calendarEvent.displayHospital}
                                            <p
                                                class={`${resizingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                                style="color:{resizingEvent.fontColor ??
                                                    '#FFF'}"
                                            >
                                                {resizingEvent.calendarEvent
                                                    .displayHospital}
                                            </p>
                                        {/if}
                                        {#if resizingEvent.calendarEvent.displaySurgeon}
                                            <p
                                                class={`${resizingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                                style="color:{resizingEvent
                                                    .calendarEvent.fontColor ??
                                                    '#FFF'}"
                                            >
                                                {resizingEvent.calendarEvent
                                                    .displaySurgeon}
                                            </p>
                                        {/if}
                                        <p
                                            class={`${resizingEvent.calendarEvent.hideDisplayText ? "invisible" : ""}`}
                                            style="color:{resizingEvent
                                                .calendarEvent.fontColor ??
                                                '#FFF'}"
                                        >
                                            {resizingEvent.start} -
                                            {resizingEvent.end}
                                        </p>
                                    {/if}
                                </div>
                            {/if}

                            <!-- {#if doctor?.eventsMap && !isObjectEmpty(doctor.eventsMap)} -->
                            {#each Object.entries(doctor?.eventsMap) as [key, value]}
                                <div class="flex flex-col items-start">
                                    {#each value.listedEvent as listRow, listRowIndex}
                                        <div
                                            class="list-row w-full"
                                            style="height: {`${doctor.eventsMap[key].eventRowHeight[listRowIndex]}px`}"
                                        >
                                            {#each listRow as list}
                                                <div
                                                    class="list-item"
                                                    draggable="true"
                                                    on:dragstart={(event) =>
                                                        handleDragStart(
                                                            event,
                                                            list,
                                                            doctor,
                                                            doctorIndex,
                                                        )}
                                                    on:dragend={handleDragEnd}
                                                    on:mousedown={(event) =>
                                                        handleMouseDown(
                                                            event,
                                                            list,
                                                            doctor,
                                                            doctorIndex,
                                                        )}
                                                    on:click={() =>
                                                        handleClickEvent(
                                                            list,
                                                            doctorIndex,
                                                        )}
                                                    style="left: calc({calculatePosition(
                                                        list.start,
                                                    )}% {isBrowserFirefox
                                                        ? ''
                                                        : '- 5px'}) ;
                                                            width: {calculateWidth(
                                                        list.start,
                                                        list.end,
                                                    )}%;
                                                            opacity: {(draggingEvent &&
                                                        draggingEvent.hasOwnProperty(
                                                            'eventType',
                                                        ) &&
                                                        draggingEvent
                                                            .calendarEvent
                                                            .id === list.id) ||
                                                    (resizingEvent &&
                                                        resizingEvent.hasOwnProperty(
                                                            'eventType',
                                                        ) &&
                                                        resizingEvent
                                                            .calendarEvent
                                                            .id === list.id)
                                                        ? '0'
                                                        : '1'};
                                                            background-color: {list.eventColour};
                                                            border: 1px solid {list.eventColour};
                                                        "
                                                >
                                                    <p
                                                        class={`${list.hideDisplayText ? "invisible" : "text-left"}`}
                                                        style="color:{list.fontColor ??
                                                            '#FFF'}"
                                                    >
                                                        {list.displayCategory}
                                                    </p>
                                                    {#if list.displayHospital}
                                                        <p
                                                            class={`${list.hideDisplayText ? "invisible" : "text-left"}`}
                                                            style="color:{list.fontColor ??
                                                                '#FFF'}"
                                                        >
                                                            {list.displayHospital}
                                                        </p>
                                                    {/if}
                                                    {#if list.displaySurgeon}
                                                        <p
                                                            class={`${list.hideDisplayText ? "invisible" : "text-left"}`}
                                                            style="color:{list.fontColor ??
                                                                '#FFF'}"
                                                        >
                                                            {list.displaySurgeon}
                                                        </p>
                                                    {/if}
                                                    <p
                                                        class={`${list.hideDisplayText ? "invisible" : "text-left"}`}
                                                        style="color:{list.fontColor ??
                                                            '#FFF'}"
                                                    >
                                                        {list.displayTime}
                                                    </p>
                                                </div>
                                            {/each}
                                        </div>
                                    {/each}
                                </div>
                            {/each}

                            {#each doctor.listedEncounter as listEncounter, encounterRowIndex}
                                <div
                                    class="flex flex-col items-start gap-y-1"
                                    style="height: {`${doctor.encounterRowHeight[encounterRowIndex]}px`}"
                                >
                                    {#each listEncounter as encounter}
                                        <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                                        <div
                                            class="encounter flex flex-col items-start"
                                            draggable="true"
                                            on:dragstart={(event) =>
                                                handleDragStart(
                                                    event,
                                                    encounter,
                                                    doctor,
                                                    doctorIndex,
                                                )}
                                            on:dragend={handleDragEnd}
                                            on:mousedown={(event) =>
                                                handleMouseDown(
                                                    event,
                                                    encounter,
                                                    doctor,
                                                    doctorIndex,
                                                )}
                                            on:click={() =>
                                                handleClickEvent(
                                                    encounter,
                                                    doctorIndex,
                                                )}
                                            on:mouseover={(event) =>
                                                showEncounterTooltip(
                                                    event,
                                                    encounter,
                                                )}
                                            on:mouseout={hideEncounterTooltip}
                                            style="
                                                left: calc({calculatePosition(
                                                encounter.start,
                                            )}% - 5px);
                                                width: {calculateWidth(
                                                encounter.start,
                                                encounter.end,
                                            )}%;
                                                opacity: {(draggingEvent &&
                                                !draggingEvent.hasOwnProperty(
                                                    'eventType',
                                                ) &&
                                                draggingEvent.calendarEvent
                                                    .id === encounter.id) ||
                                            (resizingEvent &&
                                                !resizingEvent.hasOwnProperty(
                                                    'eventType',
                                                ) &&
                                                resizingEvent.calendarEvent
                                                    .id === encounter.id)
                                                ? '0'
                                                : '1'};
                                                border: 2px solid {encounter.eventColour};
                                                color: black;
                                                {encounter.hideDisplayText
                                                ? `background-color: ${encounter.eventColour};`
                                                : `background: linear-gradient(to right, ${encounter.eventColour} 10px, #ffffff 10px);`}
                                                max-height: {DailyPlannerSingleEventMaxHeightPx}px;
                                            "
                                        >
                                            <div
                                                class="flex flex-col gap-x-2 h-full overflow-hidden ml-4"
                                            >
                                                <p
                                                    class={`${encounter.hideDisplayText ? "invisible" : "font-bold break-all"}`}
                                                >
                                                    {encounter.start} - {encounter.end}
                                                </p>

                                                <p
                                                    class={`${encounter.hideDisplayText ? "invisible" : "break-all"}`}
                                                >
                                                    <span class="font-bold"
                                                        >H:
                                                    </span>
                                                    {encounter.displayHospital}
                                                </p>

                                                <p
                                                    class={`${encounter.hideDisplayText ? "invisible" : "break-all"}`}
                                                >
                                                    <span class="font-bold"
                                                        >S:
                                                    </span>
                                                    {encounter.displaySurgeon}
                                                </p>
                                            </div>
                                        </div>
                                    {/each}
                                </div>
                            {/each}
                        </div>
                    {/each}

                    <div class="scroll-container-line"></div>
                </div>
            </div>

            <DailyCalendarEncounterTooltip
                {tooltipData}
                {showTooltip}
                {tooltipPosition}
            />
        </div>
    {/if}

    <DailyCalendarExpandSidePanel
        encounterProps={selectedEncounterProps}
        {closeExpandSidePanel}
    />
</div>

<NotificationModal
    isModalOpen={isCreateEventModalOpen}
    title="Create Event"
    closeModal={closeModifyEventModal}
    confirmAction={createEvent}
    isConfirming={isCreatingEvent}
    modalMaxWidth="max-w-[640px]"
    closeModalFunction={closeModifyEventModal}
    noContentPadding
>
    <div class="flex flex-col flex-1 gap-y-4 overflow-y-auto p-6">
        <div class="flex flex-1 gap-x-4 items-center">
            <p class="w-32">Doctor:</p>
            <p>{createEventProps?.doctor?.name}</p>
        </div>

        <div class="flex flex-1 gap-x-4 items-center">
            <p class="w-32">Selected Time:</p>

            <input
                type="time"
                placeholder="From"
                bind:value={createEventProps.start}
                class="input input-sm input-bordered"
            />
            -
            <input
                type="time"
                placeholder="to"
                class="input input-sm input-bordered"
                bind:value={createEventProps.end}
            />

            <p>
                {getDateFromDateString(calendarDate, undefined, true, true)}
            </p>
        </div>

        <div class="flex flex-1 gap-x-4 items-center">
            <p class="w-32">Event Type:</p>

            <select
                class="select select-sm select-bordered flex-1"
                bind:value={createEventProps.eventType}
            >
                <option value="encounter">Create Patient Encounter</option>
                <option value="leave">Set Leave</option>
                <option value="list">List</option>
                <option value="onCall">On-Call</option>
            </select>
        </div>

        {#if createEventProps.eventType !== "encounter"}
            <div class="flex flex-1 gap-x-4 items-center">
                <p class="w-32">Event Category:</p>

                <CalendarEventCategorySelector
                    bind:value={createEventProps.eventCategory}
                    widthClass="flex-1"
                />
            </div>
        {/if}

        {#if createEventProps.eventType == "list" || createEventProps.eventType == "onCall"}
            <div class="flex flex-1 gap-x-4 items-cente">
                <p class="w-32">Repeating:</p>

                <div class="flex flex-1 items-center justify-between">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={createEventProps.isRepeating}
                        on:change={() =>
                            handleToggleRecurrenceModalOpen(createEventProps)}
                    />

                    {#if createEventProps.isRepeating}
                        <div
                            class="flex items-center gap-x-2 cursor-pointer"
                            on:click={() =>
                                handleToggleRecurrenceModalOpen(
                                    createEventProps,
                                )}
                        >
                            <i class={editIcon}></i>
                            <p>Edit</p>
                        </div>
                    {/if}
                </div>
            </div>
        {/if}

        {#if createEventProps.eventType == "list"}
            <div
                class="flex flex-row flex-1 gap-x-4 items-center"
                use:mapSelectionFromEventData
            >
                <p class="w-32">Hospital:</p>
                <!-- width set to prevent long options go too wide -->
                <div class="flex flex-1 w-96">
                    <Select
                        bind:value={selectedHospital}
                        class="flex flex-1 max-h-[35px] rounded-full"
                        loadOptions={loadHospitals}
                        items={hospitalOptions}
                        {groupBy}
                        itemId="id"
                        label="name"
                        noOptionsMessage="No hospital found"
                        placeholder="Hospital"
                        loading={isLoadingSuggestedHospitals}
                        on:focus={loadSuggestedHospitals}
                    />
                </div>
            </div>

            <div class="flex flex-row flex-1 gap-x-4 items-center">
                <p class="w-32">Surgeon:</p>
                <!-- width set to prevent long options go too wide -->
                <div class="flex flex-1 w-96">
                    <Select
                        bind:value={selectedSurgeon}
                        class="flex flex-1 max-h-[35px] rounded-full"
                        items={surgeonOptions}
                        itemId="id"
                        label="displayLabel"
                        {groupBy}
                        noOptionsMessage="No surgeon/referrer found"
                        placeholder="Surgeon/Referrer"
                        loading={isLoadingSuggestedAccountDoctors}
                        on:focus={loadSuggestedAccountDoctor}
                    />
                </div>
            </div>

            <div class="flex flex-1 w-full">
                <p
                    class="underline cursor-pointer text-secondary"
                    on:click={openCreateNewSurgeonModal}
                >
                    Add new surgeon/referrer
                </p>
            </div>
        {/if}

        {#if createEventProps.eventType !== "encounter"}
            <div class="flex flex-1 gap-x-4 items-center">
                <p class="w-32">Description:</p>
                <div class="flex flex-1 w-full">
                    <textarea
                        class="textarea textarea-bordered w-full"
                        rows="6"
                        autocomplete="off"
                        bind:value={createEventProps.description}
                    />
                </div>
            </div>
        {/if}
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isUpdateEventTimeModalOpen}
    title="Edit Event"
    closeModal={closeModifyEventModal}
    cancelText="Cancel"
    isConfirmDisabled={isUpdateEventTimeButtonDisabled}
    confirmAction={updateSelectedEvent}
    submitText={isUpdatingEvent ? "Updating" : "Update"}
    isConfirming={isUpdatingEvent}
    closeModalFunction={closeModifyEventModal}
>
    <div class="flex flex-col gap-y-4">
        {#if moveType == "drag"}
            <p class="leading-6">
                Change this
                <b>
                    {draggingEvent.hasOwnProperty("eventType")
                        ? getAccountCalendarEventType(draggingEvent.eventType)
                        : "encounter"}
                </b>
                time to: {getDateFromDateString(
                    calendarDate,
                    undefined,
                    true,
                    true,
                )}, {draggingEvent.newStart} - {draggingEvent.newEnd}
            </p>

            {#if draggingEvent.hasDoctorReassigned}
                <p>
                    Re-assign this
                    <b>
                        {draggingEvent.hasOwnProperty("eventType")
                            ? getAccountCalendarEventType(
                                  draggingEvent.eventType,
                              )
                            : "encounter"}
                    </b>
                    to {displayDoctors[draggingEvent.newDoctorIndex].name}
                </p>
            {/if}

            {#if draggingEvent.calendarEvent?.nestedEncounter?.length}
                <p class="text-secondary font-medium">
                    Nested encounter dates will adjust accordingly
                </p>
            {/if}

            {#if draggingEvent.calendarEvent?.isRepeating}
                <div class="flex flex-col gap-y-4 font-medium">
                    <p class="text-secondary">
                        Yor are editing a repeating event, would you like to
                        edit
                    </p>
                    <label class="flex items-center gap-4">
                        <input
                            type="radio"
                            class="radio radio-secondary"
                            value={EditRepeatingEventOptions.thisOnly}
                            bind:group={editRepeatingEventType}
                        />
                        This event only
                    </label>
                    <label class="flex items-center gap-4">
                        <input
                            type="radio"
                            class="radio radio-secondary"
                            value={EditRepeatingEventOptions.thisAndFollowing}
                            bind:group={editRepeatingEventType}
                        />
                        This and following events
                    </label>
                </div>
            {/if}
        {:else if moveType == "resize"}
            <p>
                Change this
                <b>
                    {resizingEvent.hasOwnProperty("eventType")
                        ? getAccountCalendarEventType(resizingEvent.eventType)
                        : "encounter"}
                </b>
                time to: {getDateFromDateString(
                    calendarDate,
                    undefined,
                    true,
                    true,
                )}, {resizingEvent.newStart} - {resizingEvent.newEnd}
            </p>

            {#if resizingEvent.hasDoctorReassigned}
                <p>
                    Re-assign this
                    <b>
                        {resizingEvent.hasOwnProperty("eventType")
                            ? getAccountCalendarEventType(
                                  resizingEvent.eventType,
                              )
                            : "encounter"}
                    </b>
                    to {displayDoctors[resizingEvent.newDoctorIndex].name}
                </p>
            {/if}

            {#if resizingEvent.calendarEvent?.isRepeating}
                <div class="flex flex-col gap-y-4 font-medium">
                    <p class="text-secondary">
                        Yor are editing a repeating event, would you like to
                        edit
                    </p>
                    <label class="flex items-center gap-4">
                        <input
                            type="radio"
                            class="radio radio-secondary"
                            value={EditRepeatingEventOptions.thisOnly}
                            bind:group={editRepeatingEventType}
                        />
                        This event only
                    </label>
                    <label class="flex items-center gap-4">
                        <input
                            type="radio"
                            class="radio radio-secondary"
                            value={EditRepeatingEventOptions.thisAndFollowing}
                            bind:group={editRepeatingEventType}
                        />
                        This and following events
                    </label>
                </div>
            {/if}
        {/if}
    </div>
</NotificationModal>

{#if isEventNestedModalOpen}
    <CalendarEventsNestedEncounterModal
        isModalOpen={isEventNestedModalOpen}
        selectedEvent={editEventObj}
        isLoading={isReloadingEvent}
        closeModal={closeNestedEncounterModal}
        {handleEditNestedEncounter}
        {handleEditEventDetails}
        {reloadReOpenCurrentEventModal}
        on:reload={loadCalendarData}
        on:duplicateEvent={startDuplicateEvent}
    />
{/if}

<ModifyAccountEventModal
    accountEventObj={editEventObj}
    id={editEventObj.id}
    nestedEncounter={editEventObj.nestedEncounter}
    isModalOpen={isModifyEventModalOpen}
    closeModal={closeModifyEventModal}
    isOpenFromCalendar
    {afterSubmit}
    dailyPlanner={true}
/>

{#if isCalendarRecurrenceModalOpen}
    <CalendarRecurrenceModal
        bind:isModalOpen={isCalendarRecurrenceModalOpen}
        bind:eventObj={createEventProps}
        startDate={calendarDate}
    />
{/if}

{#if isCreateNewSurgeonModalOpen}
    <CreateSurgeonModal
        {isCreateNewSurgeonModalOpen}
        reloadSurgeonDetails={() => loadAccountDoctors(currentEventDoctorId)}
        closeFunc={closeCreateNewSurgeonModal}
        bind:selectedSurgeon
        doctorId={currentEventDoctorId}
        existedOptions={surgeonOptions}
    />
{/if}

<style>
    .table-container {
        display: flex;
    }

    .fixed-column {
        position: sticky;
        top: 0;
        left: 0;
        background-color: white;
        z-index: 10;
        border-right: 1px solid #ccc;
        overflow-y: scroll;
    }

    .fixed-column::-webkit-scrollbar {
        display: none;
    }
    .fixed-column {
        scrollbar-width: none;
    }

    .scroll-container {
        display: grid;
        grid-template-columns: repeat(48, 1fr);
        grid-template-rows: repeat(4, auto);
        overflow: scroll;
        width: 100%;
        position: relative;
        align-content: flex-start;
    }

    .table-header,
    .table-cell {
        border-bottom: 1px solid #ccc;
        text-align: center;
        min-width: 20px;
        position: relative;
        height: 36px;
        box-sizing: border-box;
    }

    .table-header.doctor-name {
        display: flex;
        align-items: baseline;
        flex-direction: row;
    }

    .table-header {
        background-color: #f0f0f0;
        font-size: 16px;
        min-width: 79px;
        position: sticky;
        align-items: baseline;
        top: 0;
        z-index: 10;
    }

    .hour-table-header {
        border-left: 1px solid #ccc;
        box-sizing: border-box;
    }

    .doctor-name {
        background-color: #f9f9f9;
        text-align: left;
        padding-left: 12px;
        padding-right: 12px;
        display: flex;
        flex-direction: row;
        transition: 0.2s linear;
    }

    .half-hour-line {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 1px;
        background-color: #ccc;
        z-index: 1;
    }

    .encounter {
        position: absolute;
        color: #fff;
        border-radius: 4px;
        padding: 2px;
        font-size: 0.8em;
        text-align: left;
        font-size: 13px;
        z-index: 2;
    }

    .encounter:hover {
        border-left-color: transparent;
        border-right-color: transparent;
    }

    .encounter:hover::before,
    .encounter:hover::after,
    .list-item:hover::before,
    .list-item:hover::after {
        content: "";
        position: absolute;
        top: 0;
        width: var(--DailyPlannerEventResizeBorderWidth);
        height: 100%;
        cursor: ew-resize;
    }

    .encounter:hover::before,
    .list-item:hover::before {
        left: -5px;
    }

    .encounter:hover::after,
    .list-item:hover::after {
        right: -5px;
    }

    .encounter[draggable="true"],
    .list-item[draggable="true"] {
        cursor: grab;
    }

    .encounter[draggable="true"]:active,
    .list-item[draggable="true"]:active {
        cursor: grabbing;
    }

    .list-row {
        position: relative;
        height: 25px;
        grid-column: span 48;
    }

    .list-item {
        position: absolute;
        background-color: green;
        border: 1px solid green;
        color: #fff;
        border-radius: 4px;
        padding: 2px;
        font-size: 0.8em;
        text-align: center;
        z-index: 2;
    }

    .hour-label {
        position: relative;
        z-index: 2;
    }

    .selection {
        background-color: blue;
        opacity: 0.5;
        position: absolute;
        z-index: 10;
        height: 90%;
    }

    .noselect {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        cursor: pointer;
    }

    .divider,
    .divider-horizontal {
        padding: 0;
        margin: 0;
        width: 0;
    }

    .rotate-back {
        transform: rotate(0deg);
        transition: 0.2s linear;
    }

    .rotate-180 {
        transform: rotate(180deg);
        transition: 0.2s linear;
    }
</style>
